import React, { useEffect, useState } from "react";
import { Search } from "react-feather";
import {
  RootStore,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import { fetchInterviewUsers } from "../../../store/features/AdminPanel/InterviewUsers/InterviewUsersApi";
import Table, { TableBody, TableHeading } from "../../../shared/Table/Table";
import { Card } from "../../../shared/Card/Card";
import { PrimaryButton } from "../../../shared/Buttons/Buttons";
import "./interviewUsersTable.scss";

const InterviewUsersTable = () => {
  const dispatch = useAppDispatch();
  const interviewUsersState = useAppSelector(
    (store: RootStore) => store.InterviewUsersSlice
  );
  const authState = useAppSelector((store: RootStore) => store.AuthSlice);
  const { interviewUsers, loading, total } = interviewUsersState;
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  useEffect(() => {
    const subdomain = authState?.tenant || authState?.auth_user?.subdomain;
    dispatch(fetchInterviewUsers({ subdomain }));
  }, [dispatch, authState?.tenant, authState?.auth_user?.subdomain]);

  // Filter users based on search term
  const filteredUsers = interviewUsers.filter((user: any) =>
    `${user.firstName || ""} ${user.lastName || ""} ${user.email || ""}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination
  const totalPages = Math.max(
    1,
    Math.ceil(filteredUsers.length / itemsPerPage)
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Check if there are more items to show
  const hasMoreItems = endIndex < filteredUsers.length;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "score-green";
    if (score >= 80) return "score-blue";
    if (score >= 70) return "score-yellow";
    return "score-red";
  };

  return (
    <Card>
      <div className="interview-users-table-container">
        {/* Search */}
        <div className="table-search">
          <div className="search-input-wrapper">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <Table full>
          <TableHeading>
            <th>User</th>
            <th>Total Interviews</th>
            <th>Avg Score</th>
            <th>Last Interview</th>
            <th>Team</th>
          </TableHeading>
          <TableBody>
            {(() => {
              if (loading.fetchInterviewUsers) {
                return (
                  <tr>
                    <td colSpan={5} className="loading-cell">
                      <div className="loading-content">
                        <div className="spinner" />
                        <span>Loading interview users...</span>
                      </div>
                    </td>
                  </tr>
                );
              }
              if (filteredUsers.length === 0) {
                return (
                  <tr>
                    <td colSpan={5} className="empty-cell">
                      <div className="empty-content">
                        No interview users found
                      </div>
                    </td>
                  </tr>
                );
              }
              return paginatedUsers.map((user: any) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {`${user.firstName || ""} ${user.lastName || ""}`
                          .split(" ")
                          .map((n: string) => n[0])
                          .join("")
                          .toUpperCase()}
                      </div>
                      <div>
                        <div className="user-name">
                          {`${user.firstName || ""} ${user.lastName || ""}`}
                        </div>
                        <div className="user-email">{user.email || ""}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="interview-count">
                      {user.totalInterviews || 0}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`score ${getScoreColor(
                        (user.avgInterviewScore || 0) * 10
                      )}`}
                    >
                      {Math.round((user.avgInterviewScore || 0) * 10)}%
                    </span>
                  </td>
                  <td>
                    {user.lastInterviewDate
                      ? new Date(user.lastInterviewDate).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )
                      : "N/A"}
                  </td>
                  <td>{user.userTeamName || "No Team"}</td>
                </tr>
              ));
            })()}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="table-pagination">
          <div className="pagination-info">
            {filteredUsers.length > 0 ? (
              <>
                Showing {startIndex + 1} to{" "}
                {Math.min(endIndex, filteredUsers.length)} of{" "}
                {filteredUsers.length} results
              </>
            ) : (
              "No results found"
            )}
          </div>
          <div className="pagination-buttons">
            <PrimaryButton
              text="Previous"
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disable={currentPage === 1}
              size="sm"
            />
            <span className="pagination-page-info">
              Page {currentPage} of {totalPages || 1}
            </span>
            <PrimaryButton
              text="Next"
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disable={!hasMoreItems || currentPage >= totalPages}
              size="sm"
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default InterviewUsersTable;
